import { setI18nLanguage } from '@/i18n/i18n';
import * as dateFnsLocales from 'date-fns/locale';
import { setDefaultOptions } from 'date-fns';
import { isDefined } from '@containex/common-utils';
import { usePrimeVue } from 'primevue/config';
import { useCookies } from '@vueuse/integrations/useCookies';
import { getLogger } from '@/logger/logger.ts';

interface UseLanguage {
    availableLanguages: string[];
    fallbackLocale: string;
    setLanguage(this: void, locale: string): Promise<void>;
    getInitialLanguageLocale(this: void): string;
    countryCodeToLocalName(this: void, code: string): string;
    getCurrentLanguageLocale(this: void): string;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const availableLanguages = (import.meta.env.VITE_AVAILABLE_LANGUAGES as string)?.split(',') ?? [];
const fallbackLocale = 'en';

const LANGUAGE_COOKIE_KEY = 'language';

const localeMapping: Record<string, string> = {
    en: 'enGB',
    fa: 'faIR',
    zh: 'zhCN',
};

// we need to map specific locals that don't work with our 2-digit codes
function mapLocale(locale: string): string {
    const mappedLocale = localeMapping[locale];
    if (mappedLocale != null) {
        return mappedLocale;
    }
    return locale;
}

export function useLanguage(): UseLanguage {
    const primevue = usePrimeVue();
    let currentLocaleCode: string | undefined;

    const cookies = useCookies([LANGUAGE_COOKIE_KEY]);

    function getInitialLanguageLocale(): string {
        const languageCookie = cookies.get<string | undefined>(LANGUAGE_COOKIE_KEY);

        let locale;
        if (languageCookie !== undefined) {
            locale = languageCookie;
        } else {
            locale = getInitialLanguageLocaleFromNavigator();
        }

        currentLocaleCode = locale;
        return locale;
    }

    return {
        availableLanguages,
        fallbackLocale,
        async setLanguage(locale: string): Promise<void> {
            // wrapper for setI18nLanguage with validation
            const validatedLocale = getAllowedLanguageLocaleOrFallback(locale);
            currentLocaleCode = validatedLocale;
            await setI18nLanguage(validatedLocale);

            void setPrimeVueLocale(primevue, validatedLocale);

            cookies.set(LANGUAGE_COOKIE_KEY, validatedLocale, { path: '/' });

            const entry =
                Object.entries(dateFnsLocales).find(([key]) => key === mapLocale(getLanguageBaseLocale(locale))) ?? [];

            setDefaultOptions({
                locale: entry[1],
            });
        },
        getInitialLanguageLocale,
        countryCodeToLocalName(code: string): string {
            const lang = getInitialLanguageLocale();
            const countryName = new Intl.DisplayNames([lang], { type: 'region' });
            return countryName.of(code.toUpperCase()) ?? code;
        },
        getCurrentLanguageLocale(): string {
            return currentLocaleCode ?? getInitialLanguageLocale();
        },
    };
}

function isLanguageVariantLocale(locale: string): boolean {
    // Use a regular expression to check if the locale has a country code or is a Chinese variant
    const chinesePattern = /^zh(?:-[A-Za-z]+)?$/;
    const otherLanguagePattern = /^[a-z]{2}(?:-[A-Z]{2})?$/;

    return chinesePattern.test(locale) || otherLanguagePattern.test(locale);
}

function getLanguageBaseLocale(locale: string): string {
    return locale.substring(0, 2);
}

function isAllowedLanguageLocale(locale: string): boolean {
    return availableLanguages.includes(locale);
}

function isAllowedLanguageVariantLocale(locale: string): boolean {
    return availableLanguages.includes(getLanguageBaseLocale(locale));
}

function getAllowedLanguageLocaleOrFallback(locale: string): string {
    if (isAllowedLanguageLocale(locale)) {
        // it's either a base language or a variant. The language is allowed
        return locale;
    } else if (isLanguageVariantLocale(locale) && isAllowedLanguageLocale(getLanguageBaseLocale(locale))) {
        // it's a language variant which is not allowed, but the base language is allowed
        return getLanguageBaseLocale(locale);
    }

    return fallbackLocale;
}

function getInitialLanguageLocaleFromNavigator(): string {
    const relevantLocales = [navigator.language, ...navigator.languages];
    const preferredLocale =
        relevantLocales
            .filter(isDefined)
            .find((lang) => isAllowedLanguageLocale(lang) || isAllowedLanguageVariantLocale(lang)) ?? fallbackLocale;

    return getAllowedLanguageLocaleOrFallback(preferredLocale);
}

async function setPrimeVueLocale(primevue: ReturnType<typeof usePrimeVue>, locale: string): Promise<void> {
    switch (locale) {
        case 'da': {
            primevue.config.locale = (await import('primelocale/js/da.js')).da;
            break;
        }
        case 'nl': {
            primevue.config.locale = (await import('primelocale/js/nl.js')).nl;
            break;
        }
        case 'en': {
            primevue.config.locale = (await import('primelocale/js/en.js')).en;
            break;
        }
        case 'et': {
            primevue.config.locale = (await import('primelocale/js/et.js')).et;
            break;
        }
        case 'fi': {
            primevue.config.locale = (await import('primelocale/js/fi.js')).fi;
            break;
        }
        case 'fr': {
            primevue.config.locale = (await import('primelocale/js/fr.js')).fr;
            break;
        }
        case 'de': {
            primevue.config.locale = (await import('primelocale/js/de.js')).de;
            break;
        }
        case 'hu': {
            primevue.config.locale = (await import('primelocale/js/hu.js')).hu;
            break;
        }
        case 'it': {
            primevue.config.locale = (await import('primelocale/js/it.js')).it;
            break;
        }
        case 'lv': {
            primevue.config.locale = (await import('primelocale/js/lv.js')).lv;
            break;
        }
        case 'lt': {
            primevue.config.locale = (await import('primelocale/js/lt.js')).lt;
            break;
        }
        case 'no': {
            primevue.config.locale = (await import('primelocale/js/nb_NO.js')).nb_NO;
            break;
        }
        case 'pl': {
            primevue.config.locale = (await import('primelocale/js/pl.js')).pl;
            break;
        }
        case 'es': {
            primevue.config.locale = (await import('primelocale/js/es.js')).es;
            break;
        }
        case 'sv': {
            primevue.config.locale = (await import('primelocale/js/sv.js')).sv;
            break;
        }
        default: {
            const logger = getLogger('primeLocale');
            logger.warn(`Unknown locale for PrimeVue: "${locale}"`);
            primevue.config.locale = (await import('primelocale/js/en.js')).en;
            break;
        }
    }
}
